// Costs logged inside an event, by budget line (Marius's event space,
// sections 4 Logistics and 7 "Cost"; review E3). One rule, used by the
// Logistics tab, the close-the-event step and the project budget.
//
//   - Contract and Order count as committed (signed, not yet invoiced).
//   - Invoice and Expense count as spent.
//   - Quote is filed for the record and counts for nothing.
//   - Used = spent + what each open contract or order still has to be
//     invoiced (its amount minus what was invoiced against it, never below 0).
//   - Left = ToR amount minus used.
//
// This is Marius's "the larger of committed or spent", applied per contract or
// order: a €5,000 contract with €2,000 invoiced against it uses €5,000, not
// €7,000; an invoice that pays no contract adds to what the contracts use.

export const COST_TYPES = ["contract", "order", "quote", "invoice", "expense"] as const;
export type CostType = (typeof COST_TYPES)[number];
export const COST_TYPE_LABELS: Record<CostType, string> = {
  contract: "Contract",
  order: "Order",
  quote: "Quote",
  invoice: "Invoice",
  expense: "Expense",
};

export const COMMITMENT_KINDS = ["contract", "order"] as const;
export type CommitmentKind = (typeof COMMITMENT_KINDS)[number];
/** Open or closed. Not stored: see commitmentOpen (only "closed by hand" is stored). */
export const COMMITMENT_STATUSES = ["open", "closed"] as const;
export type CommitmentStatus = (typeof COMMITMENT_STATUSES)[number];

export type CostCounts = "committed" | "spent" | "not_counted";
export const COST_COUNTS_LABELS: Record<CostCounts, string> = { committed: "Committed", spent: "Spent", not_counted: "Not counted" };

/** What a cost of this type counts as. */
export function costCounts(type: CostType): CostCounts {
  if (type === "contract" || type === "order") return "committed";
  if (type === "invoice" || type === "expense") return "spent";
  return "not_counted";
}

/**
 * A contract or order is open unless someone closed it by hand, or the
 * approved and paid invoices against it (after exclusions) reach its amount.
 * Worked out on every read, so sending an invoice back, approving it only in
 * part or raising the amount opens it again by itself.
 */
export function commitmentOpen(c: { amountCents: number; closedByHand: boolean }, approvedInvoicedCents: number): boolean {
  return !c.closedByHand && approvedInvoicedCents < c.amountCents;
}

/** A signed contract or order: open or not, and what has been invoiced against it so far. */
export type CommitmentMoney = { amountCents: number; open: boolean; invoicedCents: number };

/**
 * What one contract or order still commits: while open, its amount minus what
 * was invoiced against it (those invoices count as spent already); closed,
 * nothing, so a contract closed by hand at €4,000 of €5,000 gives the €1,000 back.
 */
export function committedOf(c: CommitmentMoney): number {
  return c.open ? Math.max(0, c.amountCents - c.invoicedCents) : 0;
}

export type CostFigures = {
  /**
   * What open contracts and orders still have to be invoiced. Shown as
   * "Committed" so that committed + spent = used, and nothing counts twice.
   */
  committedCents: number;
  spentCents: number;
  /** Spent plus what is still committed: what the line uses of its ToR amount. */
  usedCents: number;
  /** ToR amount minus used; null when there is no ToR amount to compare with. */
  leftCents: number | null;
  /** Used as a share of the ToR amount (whole percent); null without a ToR amount above 0. */
  usePct: number | null;
};

/** The money rule for one budget line of an event, or for the event as a whole. */
export function costFigures(input: { torCents: number | null; commitments: readonly CommitmentMoney[]; spentCents: readonly number[] }): CostFigures {
  const committedCents = input.commitments.reduce((s, c) => s + committedOf(c), 0);
  const spentCents = input.spentCents.reduce((s, x) => s + x, 0);
  const usedCents = spentCents + committedCents;
  const tor = input.torCents;
  return {
    committedCents,
    spentCents,
    usedCents,
    leftCents: tor === null ? null : tor - usedCents,
    usePct: tor !== null && tor > 0 ? Math.round((usedCents / tor) * 100) : null,
  };
}

/** A line is close to its limit from this share of its ToR amount on (Marius: "close to its limit"). */
export const COST_WARNING_PCT = 90;

/** Adds up per-line figures for the event total (left: the sum of the lines' left, so each line keeps its own rule). */
export function sumCostFigures(lines: readonly CostFigures[], torCents: number | null): CostFigures {
  const committedCents = lines.reduce((s, l) => s + l.committedCents, 0);
  const spentCents = lines.reduce((s, l) => s + l.spentCents, 0);
  const usedCents = lines.reduce((s, l) => s + l.usedCents, 0);
  return {
    committedCents,
    spentCents,
    usedCents,
    leftCents: torCents === null ? null : torCents - usedCents,
    usePct: torCents !== null && torCents > 0 ? Math.round((usedCents / torCents) * 100) : null,
  };
}
